import type {
  SupportedQueryParameters,
  SupportedBooleanQueryParameterKeys,
} from "common-types";

/**
 * Get the current value of the query param name passed in. Returns null if the param is not present
 * in the url.
 *
 * See the full list of allowed parameters in the common-types page at {@link AdEngineQueryParams}
 *
 * @param name The query param to retrieve
 * @returns The value of the query param if present or null if the param is not present in the
 * current pages url.
 */
export function getQueryParam<
  Name extends keyof SupportedQueryParameters,
  Value = SupportedQueryParameters[Name],
>(name: Name): Value | null {
  return new URLSearchParams(window.location.search).get(name) as Value | null;
}

/**
 * Gets the true/false value of a Boolean parameter. This allows for quick access to a boolean
 * query parameter that resolves the various truthy and falsy values but using the existence of the
 * query parameter on the search string as true instead of false.
 *
 * @param name The query param to retrieve, must be the key of a query param with type
 * `BooleanQueryParam` in {@link AdEngineQueryParams}.
 * @returns True if the query param is present on the url with no value, or has a value that is
 * either "1" or "true". If the param is not provided or set to "0" or "false", it will return false
 */
export function getBooleanQueryParam<
  Name extends SupportedBooleanQueryParameterKeys,
>(name: Name): boolean {
  const value = getQueryParam(name);
  return (
    (hasQueryParam(name) && value === "") || value === "1" || value === "true"
  );
}

/**
 * Returns if the query param is found on the URL at all (regardless of value). Returns false if the
 * param is not present.
 *
 * @param name The query param to check for
 * @returns True if the param is found on the url, false otherwise
 */
export function hasQueryParam<Name extends keyof SupportedQueryParameters>(
  name: Name,
): boolean {
  return new URLSearchParams(window.location.search).has(name);
}
